import { z } from 'zod';

export const adminIdSchema = z.string().trim().min(1).max(120);
export const listInputSchema = z.object({
  status: z.enum(['draft', 'published', 'archived']),
  limit: z.number().int().min(1).max(100).optional(),
  cursor: z.string().min(1).max(2000).optional(),
});
export const productListInputSchema = listInputSchema.extend({
  categoryId: adminIdSchema.optional(),
  exactSku: z.string().trim().min(2).max(64).optional(),
  exactSlug: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .optional(),
});
export const lifecycleInputSchema = z.object({ expectedVersion: z.number().int().positive() });
export const auditInputSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  cursor: z.string().min(1).max(2000).optional(),
});

const money = z.object({ amountMinor: z.number().int().nonnegative(), currency: z.literal('USD') });
const media = z.object({
  id: z.string(),
  kind: z.literal('image'),
  role: z.enum(['primary', 'gallery', 'thumbnail', 'customization-preview']),
  objectKey: z.string(),
  altText: z.string(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  contentType: z.string().optional(),
  displayOrder: z.number().int(),
});
const seo = z.object({ title: z.string().optional(), description: z.string().optional() });
export const adminCategorySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  shortDescription: z.string(),
  description: z.string(),
  status: z.enum(['draft', 'published', 'archived']),
  displayOrder: z.number().int(),
  heroMedia: media.optional(),
  seo,
  version: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().optional(),
  archivedAt: z.string().optional(),
});
const optionValue = z.object({
  id: z.string(),
  value: z.string(),
  label: z.string(),
  displayOrder: z.number().int(),
  priceAdjustment: money.optional(),
  active: z.boolean(),
});
const option = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  required: z.boolean(),
  displayOrder: z.number().int(),
  values: z.array(optionValue),
});
const customization = z.object({
  mode: z.enum(['none', 'wood-engraving', 'ornament', 'tshirt-print']),
  textInputs: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      required: z.boolean(),
      maxLength: z.number().int(),
      multiline: z.boolean(),
      placeholder: z.string().optional(),
    }),
  ),
  imageUpload: z
    .object({
      required: z.boolean(),
      acceptedContentTypes: z.array(z.string()),
      maxFiles: z.number().int(),
      maxFileSizeBytes: z.number().int(),
    })
    .optional(),
  designArea: z
    .object({
      width: z.number(),
      height: z.number(),
      unit: z.literal('px'),
      safeAreaInset: z.number().optional(),
    })
    .optional(),
  customerNotesAllowed: z.boolean(),
});
export const adminProductSchema = z.object({
  id: z.string(),
  slug: z.string(),
  sku: z.string(),
  categoryId: z.string(),
  productType: z.enum(['wood-engraving', 'ornament', 'tshirt']),
  name: z.string(),
  shortDescription: z.string(),
  description: z.string(),
  status: z.enum(['draft', 'published', 'archived']),
  basePrice: money,
  compareAtPrice: money.optional(),
  badges: z.array(z.enum(['bestseller', 'new', 'popular', 'seasonal'])),
  featured: z.boolean(),
  featuredOrder: z.number().int().optional(),
  merchandisingOrder: z.number().int(),
  customizable: z.boolean(),
  customization,
  options: z.array(option),
  variants: z.array(
    z.object({
      id: z.string(),
      sku: z.string(),
      optionValueIds: z.array(z.string()),
      priceOverride: money.optional(),
      active: z.boolean(),
    }),
  ),
  media: z.array(media),
  seo,
  version: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().optional(),
  archivedAt: z.string().optional(),
});
export const auditRecordSchema = z.object({
  id: z.string(),
  entityType: z.enum(['category', 'product']),
  entityId: z.string(),
  action: z.enum([
    'category-created',
    'category-updated',
    'category-published',
    'category-archived',
    'product-created',
    'product-updated',
    'product-published',
    'product-archived',
    'product-duplicated',
  ]),
  actorUserId: z.string(),
  actorRoles: z.array(z.enum(['customer', 'admin', 'workshop'])),
  occurredAt: z.string(),
  entityVersion: z.number().int(),
  summary: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  requestId: z.string().optional(),
});
export const pageSchema = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });
